import { FC, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSlideInScrollAnchor } from 'lib/hooks/useSlideInScrollAnchor';
import {
  Button,
  DataTable,
  InlineNotification,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tile,
} from '@carbon/react';
import {
  Add,
  CheckmarkOutline,
  Close,
  Edit,
  TrashCan,
  Undo,
} from '@carbon/react/icons';
import { Link } from 'components/link/link';
import { StatusTag } from 'components/status-tag';
import type { InterestDetailFields, InterestRow } from './types-interest';
import {
  confirmInterestAtom,
  fetchInterestOverviewAtom,
  interestOverviewDataAtom,
  interestOverviewErrorAtom,
  interestOverviewLoadingAtom,
  interestOverviewUrl,
  removeInterestAtom,
  restoreInterestAtom,
  revertInterestAtom,
} from './atoms/interests';
import {
  deriveInterestSectionStatus,
  formatInterest,
  isMutedInterestStatus,
  toPartyStatus,
} from './interest-utils';
import { InterestEditPanel } from './interest-edit-panel';
import shell from '../../afe-catalog/structuring/structuring-module.module.scss';

// DO Interest Review overview (aggregate tiles + per-line table). Matches the DO
// v2 mock: per-row inline Actions (Mark-Correct/Undo, Edit, Delete) with Edit
// opening a right side panel — there is NO drill-in detail page. Mirrors
// `afe-catalog/structuring/cost-overview-section.tsx` for the tiles + Carbon
// DataTable shell; the lifecycle actions live on the row (each InterestRow
// carries its HATEOAS confirmation/restoration/detail hrefs).
//
// The overview data is fetched once by the shell (StructuringModule) into the
// shared module-level atom; this section reads that atom and re-fetches only
// after a mutation (the wells section follows the same shell-fetches pattern).

interface Props {
  documentId: number | string;
}

// Column order/labels aligned to the DO v2 mock Interest Review table. NOTE: the
// mock also shows "Product" and "Owner Number" columns, but the BE interest
// contract (types-interest.ts) carries neither — omitted here rather than
// fabricated (pending a BE field addition).
const HEADERS = [
  { key: 'status', header: 'Status' },
  { key: 'propertyName', header: 'Property Name' },
  { key: 'uwi', header: 'Well UWI/API' },
  { key: 'propertyNumber', header: 'Property No.' },
  { key: 'operator', header: 'Operator' },
  { key: 'owner', header: 'Owner' },
  { key: 'interestType', header: 'Interest Type' },
  { key: 'interest', header: 'Interest Percentage' },
  { key: 'actions', header: 'Actions' },
];

// Seed an edit-panel detail from an overview row. The row carries every editable
// field the panel edits (interest / interestType / owner / operator, plus the
// read-only property identity); doPropertyId is intentionally not edited by the
// panel (see interest-edit-panel), so it is left null here.
const rowToDetail = (r: InterestRow): InterestDetailFields => ({
  propertyName: r.propertyName,
  propertyNumber: r.propertyNumber,
  uwi: r.uwi,
  importedWell: r.importedWell,
  operator: r.operator,
  owner: r.owner,
  interest: r.interest,
  interestType: r.interestType,
  doPropertyId: null,
});

export const InterestOverviewSection: FC<Props> = ({ documentId }) => {
  const data = useAtomValue(interestOverviewDataAtom);
  const loading = useAtomValue(interestOverviewLoadingAtom);
  const loadError = useAtomValue(interestOverviewErrorAtom);
  const reload = useSetAtom(fetchInterestOverviewAtom);

  const confirmInterest = useSetAtom(confirmInterestAtom);
  const revertInterest = useSetAtom(revertInterestAtom);
  const removeInterest = useSetAtom(removeInterestAtom);
  const restoreInterest = useSetAtom(restoreInterestAtom);

  const href = interestOverviewUrl(documentId);

  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<InterestRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<InterestRow | null>(null);

  // The Add/Edit panel is a Carbon `SidePanel` in `slideIn` mode, which sets
  // `margin-inline-end` on the whole `.do-structuring-shell` when it opens. That
  // narrows every section; the ones above this table reflow taller and, since
  // the window keeps its scroll offset, this section gets shoved out of view
  // (the page appeared to jump up to the Wells section). Anchoring this
  // section's root holds its viewport position across the panel's open/close
  // animation. `captureAnchor` records the pre-toggle top at click time (see the
  // hook for why it can't be read from inside the open effect). Every open/close
  // path goes through the helpers below so the anchor is always captured first.
  // (The delete confirm is a centered Modal — no margin push — so it is excluded.)
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorTopRef = useRef<number | null>(null);
  const captureAnchor = () => {
    anchorTopRef.current = rootRef.current?.getBoundingClientRect().top ?? null;
  };
  useSlideInScrollAnchor(addOpen || editRow !== null, rootRef, anchorTopRef);

  const openAdd = () => {
    captureAnchor();
    setAddOpen(true);
  };
  const closeAdd = () => {
    captureAnchor();
    setAddOpen(false);
  };
  const openEdit = (row: InterestRow) => {
    captureAnchor();
    setEditRow(row);
  };
  const closeEdit = () => {
    captureAnchor();
    setEditRow(null);
  };
  // Lifecycle-action error surface (Mark-Correct / Undo / Restore). Without it a
  // 4xx/5xx left the row unchanged with no feedback. Mirrors the wells section.
  const [actionError, setActionError] = useState<string | null>(null);
  // Delete errors show inside the confirm modal, which stays open on failure so
  // the user can retry or cancel (rather than closing before the outcome).
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const interests = useMemo(() => data?.interests ?? [], [data?.interests]);
  const summary = data?.summary;

  // Single source of truth for the section status — shared with the shell's
  // nav-tree node so the two can never disagree (removed lines excluded from
  // both counts). See deriveInterestSectionStatus.
  const {
    status: sectionStatus,
    confirmedCount,
    totalCount,
  } = deriveInterestSectionStatus(interests);

  const rows = interests.map((i) => ({
    id: i.id,
    status: '',
    propertyName: i.propertyName ?? '—',
    uwi: i.uwi ?? '—',
    propertyNumber: i.propertyNumber ?? '—',
    operator: i.operator ?? '—',
    owner: i.owner ?? '—',
    interestType: i.interestType ?? '—',
    interest: formatInterest(i.interest),
    actions: '',
  }));

  const interestsById = useMemo(() => {
    const map = new Map<string, InterestRow>();
    interests.forEach((i) => map.set(i.id, i));
    return map;
  }, [interests]);

  // Stable across shell re-renders (the shell re-renders on scroll-spy): keyed on
  // editRow so the edit panel's seeding effect doesn't re-run — and wipe in-
  // progress typing — every time the parent re-renders.
  const editDetail = useMemo(
    () => (editRow ? rowToDetail(editRow) : undefined),
    [editRow]
  );

  const afterMutation = () => reload(href);

  const markCorrect = (row: InterestRow) => {
    const url = row.resources?.confirmation?.href;
    if (!url) return;
    setActionError(null);
    confirmInterest({
      url,
      method: 'POST',
      onSuccess: afterMutation,
      onError: (m) =>
        setActionError(m ?? 'Failed to mark the line as correct.'),
    });
  };
  const removeCorrect = (row: InterestRow) => {
    const url = row.resources?.confirmation?.href;
    if (!url) return;
    setActionError(null);
    revertInterest({
      url,
      method: 'DELETE',
      onSuccess: afterMutation,
      onError: (m) =>
        setActionError(m ?? 'Failed to remove the correct status.'),
    });
  };
  const restore = (row: InterestRow) => {
    const url = row.resources?.restoration?.href;
    if (!url) return;
    setActionError(null);
    restoreInterest({
      url,
      method: 'POST',
      onSuccess: afterMutation,
      onError: (m) => setActionError(m ?? 'Failed to restore the line.'),
    });
  };
  const confirmDelete = () => {
    const url = deleteRow?.resources?.detail?.href;
    if (!url) {
      setDeleteRow(null);
      return;
    }
    setDeleteError(null);
    removeInterest({
      url,
      method: 'DELETE',
      // Keep the modal open until the outcome is known: close only on success.
      onSuccess: () => {
        setDeleteRow(null);
        setDeleteError(null);
        afterMutation();
      },
      onError: (m) => setDeleteError(m ?? 'Failed to delete the line item.'),
    });
  };

  // Per-row lifecycle actions (mock: Mark-Correct/Undo, Edit, Delete). Icon-only
  // ghost buttons; Edit opens the side panel, Delete opens a confirm modal.
  const renderActions = (interest: InterestRow) => {
    // A well removed in the Wells Information section mutes its interest line(s):
    // struck-through and read-only. Restoration happens in the Wells section (undo
    // the WELL, not the line), so a well-muted line carries no row-level actions.
    if (interest.status === 'well_removed') return null;
    if (interest.status === 'removed') {
      return (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Undo}
          iconDescription="Undo remove"
          tooltipPosition="left"
          onClick={() => restore(interest)}
        />
      );
    }
    const confirmed = interest.status === 'confirmed';
    return (
      // inline-flex wrapper keeps the icon buttons on ONE line (mirrors the Well
      // section's rowActions cell); bare buttons in the cell wrap otherwise.
      <div className={shell.rowActions}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Edit}
          iconDescription={confirmed ? 'Undo confirmation to edit' : 'Edit'}
          tooltipPosition="left"
          disabled={confirmed}
          onClick={() => openEdit(interest)}
        />
        {confirmed ? (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Remove correct status"
            tooltipPosition="left"
            onClick={() => removeCorrect(interest)}
          />
        ) : (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={CheckmarkOutline}
            iconDescription="Mark as correct"
            tooltipPosition="left"
            disabled={!interest.resources?.confirmation?.href}
            onClick={() => markCorrect(interest)}
          />
        )}
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={TrashCan}
          iconDescription="Delete"
          tooltipPosition="left"
          onClick={() => {
            setDeleteError(null);
            setDeleteRow(interest);
          }}
        />
      </div>
    );
  };

  return (
    <div className={shell.overviewBody} ref={rootRef}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Interest Review</h4>
        {summary && (
          <StatusTag
            status={sectionStatus}
            progress={`${confirmedCount}/${totalCount}`}
          />
        )}
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
          style={{ maxWidth: '100%' }}
        />
      )}

      {summary && (
        <div className={shell.summaryTiles}>
          <Tile>
            <p className={shell.tileCount}>{summary.total}</p>
            <p className={shell.tileLabel}>Total</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>{summary.confirmed}</p>
            <p className={shell.tileLabel}>Confirmed</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>{summary.notStarted}</p>
            <p className={shell.tileLabel}>Not Started</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>{summary.edited}</p>
            <p className={shell.tileLabel}>Edited</p>
          </Tile>
        </div>
      )}

      {loading && !data ? (
        <p className={shell.auditLogEmpty}>Loading interests…</p>
      ) : loadError && !data ? (
        // A failed load must NOT fall through to an empty table — on catalog-
        // gating data, "looks empty" reading as "is empty" is the dangerous
        // failure. Show the error + a Retry instead.
        <div>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Unable to load interest lines"
            subtitle="Something went wrong loading this section."
            style={{ maxWidth: '100%' }}
          />
          <Button kind="tertiary" size="sm" onClick={() => reload(href)}>
            Retry
          </Button>
        </div>
      ) : (
        <DataTable rows={rows} headers={HEADERS}>
          {({
            rows: dtRows,
            headers,
            getHeaderProps,
            getTableProps,
            onInputChange,
          }: any) => (
            <TableContainer>
              <TableToolbar size="sm">
                <TableToolbarContent>
                  <TableToolbarSearch
                    onChange={onInputChange}
                    placeholder="Search line items…"
                    persistent
                    size="sm"
                  />
                  <Button
                    kind="primary"
                    size="sm"
                    renderIcon={Add}
                    onClick={openAdd}
                  >
                    Add Line Item
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()} size="sm">
                <TableHead>
                  <TableRow>
                    {headers.map((h: any) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.map((row: any) => {
                    const interest = interestsById.get(row.id);
                    if (!interest) return null;
                    return (
                      // A muted line stays in the table, struck-through, rather than
                      // disappearing. A soft-deleted ('removed') line is undoable via
                      // its row Undo action; a 'well_removed' line (its well removed
                      // in the Wells section) is read-only and un-mutes when the well
                      // is restored there. Mirrors the Well section's rowRemoved.
                      <TableRow
                        key={row.id}
                        className={
                          isMutedInterestStatus(interest.status)
                            ? shell.rowRemoved
                            : undefined
                        }
                      >
                        <TableCell>
                          <StatusTag
                            status={toPartyStatus(interest.status)}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell>{interest.propertyName ?? '—'}</TableCell>
                        {/* Canonical Well → its UWI, hyperlinked to the well page
                            (same /wells/:uwi target the DO catalog uses). An
                            ImportedWell match has no UWI and no well page, so its
                            API number (else name) renders as plain text; else em dash. */}
                        <TableCell>
                          {interest.uwi ? (
                            <Link to={`/wells/${interest.uwi}`}>
                              {interest.uwi}
                            </Link>
                          ) : (
                            (interest.importedWell?.apiNumber ??
                            interest.importedWell?.wellName ??
                            '—')
                          )}
                        </TableCell>
                        <TableCell>{interest.propertyNumber ?? '—'}</TableCell>
                        <TableCell>{interest.operator ?? '—'}</TableCell>
                        <TableCell>{interest.owner ?? '—'}</TableCell>
                        <TableCell>{interest.interestType ?? '—'}</TableCell>
                        <TableCell>
                          {formatInterest(interest.interest)}
                        </TableCell>
                        <TableCell>{renderActions(interest)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      {/* Add (create) */}
      <InterestEditPanel
        open={addOpen}
        mode="create"
        documentId={documentId}
        onClose={closeAdd}
        onSaved={() => {
          closeAdd();
          reload(href);
        }}
      />

      {/* Edit (from a row) */}
      <InterestEditPanel
        open={editRow !== null}
        mode="edit"
        documentId={documentId}
        lineItemId={editRow?.id}
        detail={editDetail}
        onClose={closeEdit}
        onSaved={() => {
          closeEdit();
          reload(href);
        }}
      />

      <Modal
        open={deleteRow !== null}
        danger
        size="sm"
        modalHeading="Delete Line Item"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setDeleteRow(null);
          setDeleteError(null);
        }}
        onSecondarySubmit={() => {
          setDeleteRow(null);
          setDeleteError(null);
        }}
        onRequestSubmit={confirmDelete}
      >
        <p>
          Are you sure you want to delete{' '}
          {deleteRow?.propertyName ?? deleteRow?.uwi ?? 'this line item'}? You
          can undo this from the removed line&rsquo;s Undo action.
        </p>
        {deleteError && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={deleteError}
            onCloseButtonClick={() => setDeleteError(null)}
            className={shell.modalErrorNotification}
          />
        )}
      </Modal>
    </div>
  );
};
